import { useRootStore } from "@/store/root";
import utils from "@/utils/utils";
import { schedulePostBrokerAffiliateStateToParent } from "@/composables/embed/postBrokerAffiliateStateToParent";

export const COVOMO_SET_BROKER_FIELDS_TYPE = "covomoSetBrokerFields";

type BrokerFieldPayload = {
  type?: string;
  broker_phone?: string;
  broker_name?: string;
  broker_company?: string;
  broker_email?: string;
  pool_email?: string;
  pool_name?: string;
  broker_id?: string;
  broker_sub_id?: string;
  vmtel?: string;
  vmname?: string;
  company?: string;
  vmmail?: string;
  poolmail?: string;
  poolname?: string;
  vmnr?: string;
  vmunr?: string;
};

function asTrimmedString(v: unknown): string | undefined {
  if (v === undefined || v === null) return undefined;
  const s = String(v).trim();
  return s;
}

/**
 * Applies broker/pool fields from a parent `postMessage` into sessionStorage + root store.
 * Canonical keys preferred; short aliases (vmtel, vmname, …) supported.
 */
export function applyBrokerFieldsFromParentPayload(
  rootStore: ReturnType<typeof useRootStore>,
  raw: BrokerFieldPayload,
): void {
  const pairs: [string, (v: string) => void][] = [
    [
      asTrimmedString(raw.broker_phone) ?? asTrimmedString(raw.vmtel) ?? "",
      (v) => {
        utils.sessionStorageSet("broker_phone", v);
        rootStore.setBrokerPhone(v);
      },
    ],
    [
      asTrimmedString(raw.broker_name) ?? asTrimmedString(raw.vmname) ?? "",
      (v) => {
        utils.sessionStorageSet("broker_name", v);
        rootStore.setBrokerName(v);
      },
    ],
    [
      asTrimmedString(raw.broker_company) ?? asTrimmedString(raw.company) ?? "",
      (v) => {
        utils.sessionStorageSet("broker_company", v);
        rootStore.setBrokerCompany(v);
      },
    ],
    [
      asTrimmedString(raw.broker_email) ?? asTrimmedString(raw.vmmail) ?? "",
      (v) => {
        utils.sessionStorageSet("broker_email", v);
        rootStore.setBrokerEmail(v);
      },
    ],
    [
      asTrimmedString(raw.pool_email) ?? asTrimmedString(raw.poolmail) ?? "",
      (v) => {
        utils.sessionStorageSet("pool_email", v);
        rootStore.setPoolEmail(v);
      },
    ],
    [
      asTrimmedString(raw.pool_name) ?? asTrimmedString(raw.poolname) ?? "",
      (v) => {
        utils.sessionStorageSet("pool_name", v);
        rootStore.setPoolName(v);
      },
    ],
    [
      asTrimmedString(raw.broker_id) ?? asTrimmedString(raw.vmnr) ?? "",
      (v) => {
        utils.sessionStorageSet("broker_id", v);
        rootStore.setBrokerId(v);
      },
    ],
    [
      asTrimmedString(raw.broker_sub_id) ?? asTrimmedString(raw.vmunr) ?? "",
      (v) => {
        utils.sessionStorageSet("broker_sub_id", v);
        rootStore.setBrokerSubId(v);
      },
    ],
  ];

  for (const [value, apply] of pairs) {
    if (value !== "") apply(value);
  }
  schedulePostBrokerAffiliateStateToParent();
}

function isCovomoSetBrokerFieldsMessage(
  data: unknown,
): data is BrokerFieldPayload {
  if (!data || typeof data !== "object") return false;
  const t = (data as BrokerFieldPayload).type;
  return t === COVOMO_SET_BROKER_FIELDS_TYPE;
}

let registeredHandler: ((ev: MessageEvent) => void) | null = null;

export function registerParentBrokerPostMessageListener(): void {
  if (typeof window === "undefined") return;
  if (registeredHandler) return;
  registeredHandler = (ev: MessageEvent): void => {
    if (ev.source !== window.parent) return;
    if (!isCovomoSetBrokerFieldsMessage(ev.data)) return;
    applyBrokerFieldsFromParentPayload(useRootStore(), ev.data);
  };
  window.addEventListener("message", registeredHandler);
}

export function unregisterParentBrokerPostMessageListener(): void {
  if (typeof window === "undefined" || !registeredHandler) return;
  window.removeEventListener("message", registeredHandler);
  registeredHandler = null;
}
